'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import SuperTokens, { SuperTokensWrapper } from 'supertokens-auth-react'
import { AuthPage, canHandleRoute, getRoutingComponent } from 'supertokens-auth-react/ui'
import EmailPassword from 'supertokens-auth-react/recipe/emailpassword'
import EmailVerification from 'supertokens-auth-react/recipe/emailverification'
import Session, {
  doesSessionExist,
  SessionAuth,
  signOut as superTokensSignOut,
} from 'supertokens-auth-react/recipe/session'
import { EmailPasswordPreBuiltUI } from 'supertokens-auth-react/recipe/emailpassword/prebuiltui'
import { EmailVerificationPreBuiltUI } from 'supertokens-auth-react/recipe/emailverification/prebuiltui'
import {
  AlertCircle,
  Archive,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  Filter,
  Link2,
  Loader2,
  LogOut,
  Megaphone,
  Menu,
  Minus,
  Moon,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Square,
  Sun,
  Upload,
  X,
  EyeOff,
} from 'lucide-react'
import Turnstile, { type TurnstileHandle } from './Turnstile'
import { ThreatDetailModal } from '@trustfence/ui'
import { ResizableTable, TableTd, type ColumnDef } from './ResizableTable'
import { OverviewView } from './OverviewView'
import { useThemePreference } from './theme'
import { Sidebar } from './shell/Sidebar'
import { Topbar } from './shell/Topbar'
import { PageChromeProvider } from './shell/pageChrome'
import { StickyStackContext } from './stickyStack'
import { apiGet } from './Entries/entriesApi'
import { BrandIntegrityView } from './Entries/BrandIntegrityView'
import { UiKitView } from './Entries/UiKitView'
import { NoticeProvider } from './ui'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import type { ProcessedTheme, ProcessedEntry, DomainDetail, BrowserRow } from './ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses, compactDate, postTypeLabel, postTypeStyle, entryDisplayTitle } from './ProcessedEntry/DomainEntry/processedTypes'
import { CampaignsView, AlertsAnalysisView, SibsPilotOverviewView } from './SibsMailView'
import { CttMailAlertView } from './CttMailAlertView'
import { CttVendorEntriesView } from './CttVendorEntriesView'
import { AdminSubmitEntryView } from './Entries/AdminSubmitEntryView'
import { JobsView } from './Entries/JobsView'
import { ActionsView } from './Entries/ActionsView'
import { ProcessedEntryCard } from './ProcessedEntry/ProcessedEntryCard'
import { fmtDatePt, processedToPreview } from './TrustCenter/CTT/cttUtils'
import { CttTrustCenterView } from './TrustCenter/CTT/CttTrustCenterView'
import { CttTrustCenterEntriesView } from './TrustCenter/CTT/CttTrustCenterEntriesView'
import { useCatalog } from './Entries/entriesApi'
import { CasosView } from './Entries/CasosView'
import { CasesDashboardView } from './Entries/CasesDashboardView'
import { UnifiedEntriesDashboardView } from './Entries/UnifiedEntriesDashboardView'
import { CampaignsDashboardView } from './Entries/CampaignsDashboardView'
import { DomainCandidatesDashboardView } from './Entries/DomainCandidatesDashboardView'
import { BACKOFFICE_NAVIGATE_EVENT } from './Entries/useDetailPath'
import { NotificationsBell } from './NotificationsBell'
import { BrandVocabularyView } from './Entries/BrandVocabularyView'
import { TokenChainView } from './Entries/TokenChainView'
import { BugReportWidget } from './BugReportWidget'
import { ManageEmailTypesView } from './Entries/ManageEmailTypesView'
import { EmailLogsView } from './Entries/EmailLogsView'
import { EmailComposeView } from './Entries/EmailComposeView'
import { ManageTenantsView } from './Entries/ManageTenantsView'
import { TenantLogsView } from './Entries/TenantLogsView'
import { ManagePlatformsView } from './Entries/ManagePlatformsView'
import { ManageSourcesView } from './Entries/ManageSourcesView'
import { ENTRIES_SLUGS } from './entriesRoutes'
import { BRANDS, brandsForPlatforms, brandCodeForView, defaultKindForView, flatViews, isGroup, slugForView, VIEW_SLUGS, type BrandEntry, type View, type FlatView, type SidebarEntry } from './dashboardCatalog'
import { EntryDetailShell, type DetailBadge } from './EntryDetail/EntryDetailShell'
import { setViewTitle } from './pageTitle'
import { EntryDetailPanel } from './EntryDetail/EntryDetailPanel'
import { SibsEntryDataWidget, SibsEmailWidget, SibsPipelineWidget, sibsCommunication } from './EntryDetail/SibsWidgets'
import { OriginalSubmissionWidget } from './ProcessedEntry/OriginalSubmissionWidget'
import { trackEvent } from '@/lib/analytics'
import { parseDbTimestamp } from '@/lib/dates'

// ─── Chart helpers for ProcessedEntriesView ───────────────────────────────────

function buildPeChartData(entries: ProcessedEntry[]) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const dayMs = 86400000
  const map = new Map<string, number>()
  for (let i = 0; i < 30; i++) {
    const d = new Date(today.getTime() - (29 - i) * dayMs)
    map.set(`${d.getDate()}/${d.getMonth() + 1}`, 0)
  }
  for (const e of entries) {
    const d = parseDbTimestamp(e.createdAt)
    if (!d) continue
    const key = `${d.getDate()}/${d.getMonth() + 1}`
    if (map.has(key)) map.set(key, (map.get(key) ?? 0) + 1)
  }
  return Array.from(map.entries()).map(([date, signals]) => ({ date, signals }))
}

function buildAllTimeChartData(entries: ProcessedEntry[]) {
  if (entries.length === 0) return []
  const today = new Date()
  const oldestTs = entries.reduce((min, e) => {
    const d = parseDbTimestamp(e.createdAt)
    return d ? Math.min(min, d.getTime()) : min
  }, Infinity)
  const oldest = new Date(oldestTs)
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const map = new Map<string, number>()
  let y = oldest.getFullYear(), m = oldest.getMonth()
  while (y < today.getFullYear() || (y === today.getFullYear() && m <= today.getMonth())) {
    map.set(`${y}-${String(m).padStart(2, '0')}`, 0)
    m++; if (m > 11) { m = 0; y++ }
  }
  for (const e of entries) {
    const d = parseDbTimestamp(e.createdAt)
    if (!d) continue
    const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, '0')}`
    if (map.has(key)) map.set(key, (map.get(key) ?? 0) + 1)
  }
  return Array.from(map.entries()).map(([key, signals]) => {
    const [yr, mo] = key.split('-').map(Number)
    return { label: `${MONTHS[mo]} ${yr}`, signals }
  })
}

const PeChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded border border-border bg-card px-3 py-2 text-[11px] font-mono shadow-notice">
      <div className="mb-1 font-semibold text-muted-foreground">{label}</div>
      <div className="flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full bg-purple-500" />
        <span className="text-muted-foreground">signals</span>
        <span className="ml-auto pl-4 font-semibold text-foreground">{payload[0].value}</span>
      </div>
    </div>
  )
}


const preBuiltUIList = [EmailPasswordPreBuiltUI, EmailVerificationPreBuiltUI]
let didInitSuperTokens = false

export function ensureBackofficeSuperTokens() {
  if (didInitSuperTokens || typeof window === 'undefined') return

  SuperTokens.init({
    appInfo: {
      appName: 'TrustFence Backoffice',
      apiDomain: window.location.origin,
      websiteDomain: window.location.origin,
      apiBasePath: '/api/auth',
      websiteBasePath: '/login',
    },
    getRedirectionURL: async (context) => {
      // SessionAuth sends a logged-out visitor to /login?redirectToPath=<the
      // URL they opened>; land them there, not on '/' (which redirects to SIBS).
      if (context.action === 'SUCCESS') return context.redirectToPath ?? '/'
      return undefined
    },
    // Same selector webapp uses (src/utils/superTokens.ts) to hide the
    // "Powered by SuperTokens" footer on the prebuilt auth UI.
    style: `
      [data-supertokens~=superTokensBranding] {
        display: none;
      }
    `,
    // The "Sign in / Sign up" switcher line shares its data-supertokens
    // selector (headerSubtitle secondaryText) with unrelated headers
    // elsewhere in the prebuilt UI (e.g. the reset-password screen's own
    // instructions), so it can't be hidden via the style override above
    // without also hiding those. Emptying just its own translation keys
    // removes the text/link without touching anything else.
    languageTranslations: {
      translations: {
        en: {
          AUTH_PAGE_HEADER_SUBTITLE_SIGN_IN_START: '',
          AUTH_PAGE_HEADER_SUBTITLE_SIGN_IN_SIGN_UP_LINK: '',
          AUTH_PAGE_HEADER_SUBTITLE_SIGN_IN_END: '',
        },
      },
    },
    recipeList: [
      EmailPassword.init({ resetPasswordUsingTokenFeature: { disableDefaultUI: false } }),
      EmailVerification.init({ mode: 'REQUIRED' }),
      Session.init(),
    ],
  })

  didInitSuperTokens = true
}

export function BackofficeLoginPage() {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    ensureBackofficeSuperTokens()
    setReady(true)
    trackEvent({ event_type: 'page', event_name: 'login', page: 'Login' })
    doesSessionExist()
      .then((exists) => { if (exists) window.location.assign('/') })
      .catch(() => null)
  }, [])

  if (!ready) return <main className="min-h-screen bg-card" />

  // <AuthPage> only ever renders the combined sign-in/sign-up view — it has
  // no logic to detect sub-routes like /reset-password or /verify-email.
  // Those are handled by a separate mechanism (canHandleRoute/
  // getRoutingComponent, SuperTokens' standard non-react-router integration
  // pattern) that must be checked explicitly, or those routes silently fall
  // through to the sign-in form regardless of a valid token in the URL.
  if (canHandleRoute(preBuiltUIList)) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 bg-card p-6">
        <SuperTokensWrapper>{getRoutingComponent(preBuiltUIList)}</SuperTokensWrapper>
      </main>
    )
  }

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-3 bg-card p-6">
      <SuperTokensWrapper>
        <AuthPage
          preBuiltUIList={preBuiltUIList}
          redirectOnSessionExists
          onSessionAlreadyExists={() => window.location.assign('/')}
          isSignUp={false}
          navigate={{
            push: (path) => { window.location.assign(path) },
            goBack: () => window.history.back(),
          }}
        />
      </SuperTokensWrapper>
      {/* Replaces the "Powered by SuperTokens" branding badge (hidden via
          the [data-supertokens~=superTokensBranding] style rule above) in
          the same footer slot below the auth card. */}
      <div className="flex items-center gap-2 opacity-80">
        <img src="/logoTF.png" alt="TrustFence" className="h-5 w-5 object-contain" />
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">TrustFence/Ops</span>
      </div>
      <BugReportWidget theme="light" />
    </main>
  )
}

// Prunes a brand's views down to only the ones this session has actually
// been granted (via the tenant_dashboard_access matrix) — Editors always see
// the full, unfiltered tree.
function grantedOnlyEntries(entries: SidebarEntry[], access: BackofficeAccess): SidebarEntry[] {
  if (access.isEditor) return entries
  const isGranted = (v: FlatView) => v.access === 'shared' && access.dashboardKeys.has(v.id)
  const keep: SidebarEntry[] = []
  for (const e of entries) {
    if (isGroup(e)) {
      const items = grantedOnlyEntries(e.items, access)
      if (items.length > 0) keep.push({ ...e, items })
    } else if (isGranted(e)) {
      keep.push({ ...e, children: (e.children ?? []).filter(isGranted) })
    } else {
      // A child granted without its parent (e.g. Old Cases without Unified
      // Entries) stands on its own rather than disappearing with it.
      keep.push(...(e.children ?? []).filter(isGranted))
    }
  }
  return keep
}

// "Entradas" (moderation) is brand-agnostic, so it lives outside BRANDS and gets
// its own top-level route (/entries/{slug}) instead of /{brand}/{slug}.
// Split into two sidebar sections: the moderation queue itself (Moderação) and the
// admin/registration forms (Administration) — both still share the /entries/*
// route namespace and slug lookup below.
// Unified Entries is the same general/editor-only dashboard the per-brand
// "Default Dashboards" groups link to (pre-scoped per brand there) — grouped
// and marked sharedTemplate here too so it gets the same Link2 indicator and
// sidebar treatment instead of looking like a one-off Moderation feature.
// The two earlier queues sit under it: Old Cases (CasesDashboardView, the
// raw_entries_flat table) and Conversation Cases (CasosView, the original
// card-per-entry queue).
const MODERATION_VIEWS: SidebarEntry[] = [
  { group: 'Default Dashboards', items: [
    { id: 'entries-unified', label: 'Unified Entries', sharedTemplate: true, children: [
      { id: 'entries-cases', label: 'Old Cases', sharedTemplate: true },
      { id: 'entries-casos', label: 'Conversation Cases' },
    ]},
    { id: 'entries-campaigns', label: 'Campaigns' },
    { id: 'entries-domains', label: 'Domain Candidates', sharedTemplate: true },
  ]},
]

const ADMINISTRATION_VIEWS: FlatView[] = [
  { id: 'entries-plataformas-manage', label: 'Manage Platforms' },
  { id: 'entries-brand-vocabulary', label: 'Brand Vocabulary', children: [{ id: 'entries-token-chain', label: 'Token Chain' }] },
  { id: 'entries-sources-manage', label: 'Manage Sources' },
  { id: 'entries-tenants-manage', label: 'Manage Tenants', children: [{ id: 'entries-tenant-logs', label: 'Logs' }] },
  { id: 'entries-email-types', label: 'Manage Email Types' },
  { id: 'entries-email-logs', label: 'Email Logs', children: [{ id: 'entries-email-compose', label: 'Compose Email' }] },
  { id: 'entries-submit-entry', label: 'Workbench' },
  { id: 'entries-jobs', label: 'Jobs' },
  { id: 'entries-actions', label: 'Actions' },
  // The design system's primitives on one page (Entries/UiKitView.tsx).
  { id: 'entries-ui-kit', label: 'UI kit' },
]

// Jobs and Actions are operations, not administration: the sidebar lists
// them first under OPERATIONS, before the brands (the mock's order).
const OPERATIONS_VIEW_IDS = new Set<View>(['entries-jobs', 'entries-actions'])
const OPERATIONS_VIEWS: FlatView[] = ADMINISTRATION_VIEWS.filter((v) => OPERATIONS_VIEW_IDS.has(v.id))
const ADMIN_ONLY_VIEWS: FlatView[] = ADMINISTRATION_VIEWS.filter((v) => !OPERATIONS_VIEW_IDS.has(v.id))

const ENTRIES_VIEWS: SidebarEntry[] = [...MODERATION_VIEWS, ...ADMINISTRATION_VIEWS]

function isEntriesView(id: View) {
  return id.startsWith('entries-')
}

function entriesViewFromTab(tab?: string): View | undefined {
  return flatViews(ENTRIES_VIEWS).find((v) => ENTRIES_SLUGS[v.id] === tab)?.id as View | undefined
}

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'trustfence-backoffice-sidebar-collapsed'

// The brands whose per-brand "Default Dashboards" (Cases / Unified Entries /
// Domain Candidates) are the shared templates pre-scoped to one platform_id
// — see brandCasesPlatformIds below for how each resolves to its id.
// Any platform's name_code. Not a closed union any more: brands are read from
// the platform table, so the set is only known at runtime.
type BrandId = string

function brandForView(brands: ReadonlyArray<BrandEntry>, id: View) {
  return brands.find((b) => flatViews(b.views).some((v) => v.id === id))
}

function findFlatView(brands: ReadonlyArray<BrandEntry>, id: View): FlatView | undefined {
  for (const b of brands) {
    const found = flatViews(b.views).find((v) => v.id === id)
    if (found) return found
  }
  return undefined
}

// The labels along the sidebar path down to a view, e.g. ['Custom Mail Alert',
// 'Entries'] — a nested view's own label ('Entries', 'Logs') means nothing on
// its own. Groups are skipped: they're section headings in the sidebar
// ('Default Dashboards', 'Raw'), not part of what the view is called. Recurses
// through children rather than reusing flatViews, which flattens the tree and
// so loses exactly the parent link this needs.
function viewLabelPath(entries: SidebarEntry[], id: View): string[] | undefined {
  for (const e of entries) {
    if (isGroup(e)) {
      const inGroup = viewLabelPath(e.items, id)
      if (inGroup) return inGroup
      continue
    }
    if (e.id === id) return [e.label]
    const inChild = e.children && viewLabelPath(e.children, id)
    if (inChild) return [e.label, ...inChild]
  }
  return undefined
}

// What the browser tab calls the current view, brand first ('CTT · Domain
// Candidates') so the per-brand default dashboards — identically labelled
// under every brand — are told apart with several tabs open. Brands cover
// their own views; the Moderation/Administration ones live outside BRANDS in
// ENTRIES_VIEWS, hence the second lookup.
function labelForView(brands: ReadonlyArray<BrandEntry>, id: View): string {
  const brand = brandForView(brands, id)
  const path = (brand ? viewLabelPath(brand.views, id) : undefined) ?? viewLabelPath(ENTRIES_VIEWS, id) ?? [id]
  return brand ? [brand.label, ...path].join(' · ') : path.join(' · ')
}

// The topbar breadcrumb: section › brand › group › view.
function crumbsForView(brands: ReadonlyArray<BrandEntry>, id: View): string[] {
  const brand = brandForView(brands, id)
  if (brand) return ['Brands', brand.label, ...(viewLabelPath(brand.views, id) ?? [id])]
  const section = OPERATIONS_VIEW_IDS.has(id) ? 'Operations' : flatViews(MODERATION_VIEWS).some((v) => v.id === id) ? 'Moderation' : 'Administration'
  return [section, ...(viewLabelPath(ENTRIES_VIEWS, id) ?? [id])]
}

// What a session is entitled to see, resolved once on mount from /my-access
// (backend/routes/backoffice/commonManagement.ts). Editors see everything, as
// before. A tenant-scoped non-Editor sees a view only if it's explicitly
// granted in dashboardKeys — a direct many-to-many grant (tenant_dashboard_access
// table, editable per-tenant in Manage Tenants' Dashboard Access matrix), not
// inferred from tenant/platform ownership. That's what lets several different
// tenants all be granted the same SIBS dashboards independently. A view also
// has to be marked access:'shared' in dashboardCatalog.ts to ever be
// grantable at all — Moderation/Administration and any 'exclusive' view stay
// Editor-only outright, no matter what the matrix says.
type BackofficeAccess = { isEditor: boolean; dashboardKeys: Set<View>; tenants?: Array<{ id: string; name: string; name_code?: string }> }

function isViewAllowed(brands: ReadonlyArray<BrandEntry>, id: View, access: BackofficeAccess): boolean {
  if (access.isEditor) return true
  if (isEntriesView(id)) return false
  if (findFlatView(brands, id)?.access !== 'shared') return false
  return access.dashboardKeys.has(id)
}

function brandHasGrantedView(brand: BrandEntry, access: BackofficeAccess): boolean {
  return flatViews(brand.views).some((v) => v.access === 'shared' && access.dashboardKeys.has(v.id))
}

function viewFromRoute(brands: ReadonlyArray<BrandEntry>, brandId?: string, dashboard?: string): View {
  const brand = brandId ? brands.find((b) => b.id === brandId || b.platformCode === brandId) : null
  if (!brand) return 'sibs-overview'

  const views = flatViews(brand.views)
  if (!dashboard) return views[0]?.id ?? 'sibs-overview'

  return views.find((v) => slugForView(v.id) === dashboard)?.id ?? views[0]?.id ?? 'sibs-overview'
}

function viewHref(brands: ReadonlyArray<BrandEntry>, id: View): string {
  if (isEntriesView(id)) return `/entries/${ENTRIES_SLUGS[id]}`
  const brand = brandForView(brands, id)
  return `/${brand?.id ?? brandCodeForView(id)}/${slugForView(id)}`
}

function Shell({ onSignOut, initialBrand, initialDashboard, initialEntriesTab }: { onSignOut: () => void; initialBrand?: string; initialDashboard?: string; initialEntriesTab?: string }) {
  // Brands are the platform table plus the hand-written BRANDS entries, so the
  // catalog has to be read before the sidebar exists. It arrives after mount;
  // until then only the hand-written brands are known (see the re-resolve
  // effect below, which fixes a deep link into a generated brand).
  const { catalog: entriesCatalog, loading: entriesCatalogLoading } = useCatalog()
  const brands = useMemo(() => brandsForPlatforms(entriesCatalog.platforms), [entriesCatalog.platforms])

  const matchedBrand = initialBrand ? brands.find((b) => b.id === initialBrand || b.platformCode === initialBrand) : null
  const resolveView = () => (initialEntriesTab !== undefined ? entriesViewFromTab(initialEntriesTab) ?? 'entries-unified' : viewFromRoute(brands, initialBrand, initialDashboard))

  const [openBrands, setOpenBrands] = useState<Set<string>>(() => new Set(matchedBrand ? [matchedBrand.id] : ['sibs', 'ctt']))
  const [view, setView] = useState<View>(resolveView)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [showAllBrands, setShowAllBrands] = useState(!matchedBrand)
  // Light by default, persisted, OS-following until chosen (theme.tsx).
  const { theme, setTheme } = useThemePreference()
  const [access, setAccess] = useState<BackofficeAccess | null>(null)

  // A route into a generated brand (/viaverde/domain-candidates) cannot
  // resolve on the first render -- that brand does not exist until the
  // platform list lands -- so it falls back to the default view. Re-resolve
  // once, when the brand it names actually shows up.
  const routeResolved = useRef(false)
  useEffect(() => {
    if (routeResolved.current || initialEntriesTab !== undefined || !initialBrand) return
    if (!brands.some((b) => b.id === initialBrand || b.platformCode === initialBrand)) return
    routeResolved.current = true
    setView(viewFromRoute(brands, initialBrand, initialDashboard))
    setOpenBrands((prev) => new Set([...prev, initialBrand]))
  }, [brands, initialBrand, initialDashboard, initialEntriesTab])

  // Resolves each brand's per-brand "Cases" (sibs-cases/ctt-cases/…) to the
  // one numeric platform_id CasesDashboardView needs — deliberately NOT
  // BRANDS[].platformCode (that's 'sibs', but the platform table's own
  // name_code for SIBS is 'mbway', same string every other SIBS view here
  // already uses — see OverviewView's platformCode="mbway" a few lines
  // down). Undefined (catalog not loaded yet, or the code genuinely isn't
  // in the platform table) must never be treated as "show every platform"
  // for these — that's the correct default for the general, editor-only
  // entries-cases, but for a tenant-facing per-brand route it would leak
  // every other brand's cases the moment resolution fails for any reason.
  const brandCasesPlatformIds: Partial<Record<BrandId, number>> = useMemo(() => {
    const out: Partial<Record<BrandId, number>> = {}
    for (const b of brands) {
      const id = entriesCatalog.platforms.find((p) => p.name_code === (b.platformNameCode ?? b.platformCode))?.id
      if (id != null) out[b.id] = id
    }
    return out
  }, [brands, entriesCatalog.platforms])

  // Brands whose platform is hidden from the public (platform.is_public =
  // false). The trust center still shows them -- that is the point -- but the
  // sidebar says so, since nothing about their entries reaches the consumer apps.
  const hiddenBrands: Set<BrandId> = useMemo(() => {
    const out = new Set<BrandId>()
    for (const b of brands) {
      const p = entriesCatalog.platforms.find((p) => p.name_code === (b.platformNameCode ?? b.platformCode))
      if (p && p.is_public === false) out.add(b.id)
    }
    return out
  }, [brands, entriesCatalog.platforms])

  // Renders a per-brand Cases route — never falls back to the unscoped
  // "show every platform" CasesDashboardView default (see the comment on
  // brandCasesPlatformIds above for why that would leak other brands' data).
  const renderBrandCases = (brand: BrandId) => {
    const platformId = brandCasesPlatformIds[brand]
    if (platformId != null) return <CasesDashboardView theme={theme} platformIds={[platformId]} storageKey={brand} isEditor={access?.isEditor} hiddenColumns={['source', 'last_pipeline_step']} />
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2">
        {entriesCatalogLoading ? (
          <span className={`font-mono text-[11px] uppercase tracking-widest animate-pulse text-subtle`}>Loading…</span>
        ) : (
          <span className={`font-mono text-[13px] text-destructive`}>
            Could not resolve this brand's platform — Cases can't be scoped safely.
          </span>
        )}
      </div>
    )
  }

  // Same reasoning/fallback as renderBrandCases, reusing the same
  // brandCasesPlatformIds lookup.
  const renderBrandUnified = (brand: BrandId) => {
    const platformId = brandCasesPlatformIds[brand]
    if (platformId != null) return <UnifiedEntriesDashboardView theme={theme} platformIds={[platformId]} storageKey={brand} isEditor={access?.isEditor} />
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2">
        {entriesCatalogLoading ? (
          <span className={`font-mono text-[11px] uppercase tracking-widest animate-pulse text-subtle`}>Loading…</span>
        ) : (
          <span className={`font-mono text-[13px] text-destructive`}>
            Could not resolve this brand's platform — Unified Entries can't be scoped safely.
          </span>
        )}
      </div>
    )
  }

  // Same reasoning/fallback for Brand Integrity: the status is computed for
  // exactly one platform, so an unresolved brand shows nothing, never
  // another brand's standing.
  const renderBrandIntegrity = (brand: BrandId) => {
    const entry = brands.find((b) => b.id === brand)
    const platformId = brandCasesPlatformIds[brand]
    if (platformId != null) return <BrandIntegrityView theme={theme} platformId={platformId} brandLabel={entry?.label ?? brand} brandId={brand} isEditor={access?.isEditor} />
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2">
        {entriesCatalogLoading ? (
          <span className={`font-mono text-[11px] uppercase tracking-widest animate-pulse text-subtle`}>Loading…</span>
        ) : (
          <span className={`font-mono text-[13px] text-destructive`}>
            Could not resolve this brand's platform — Brand Integrity can't be scoped safely.
          </span>
        )}
      </div>
    )
  }

  // Same reasoning/fallback again for the per-brand Domain Candidates view
  // (auto_domain_* candidates are platform-scoped, never widened to a tenant's
  // other platforms — see backend/queries/autoDomains.ts).
  const renderBrandDomains = (brand: BrandId) => {
    const platformId = brandCasesPlatformIds[brand]
    if (platformId != null) return <DomainCandidatesDashboardView theme={theme} platformIds={[platformId]} storageKey={`${brand}-domains`} isEditor={access?.isEditor} />
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2">
        {entriesCatalogLoading ? (
          <span className={`font-mono text-[11px] uppercase tracking-widest animate-pulse text-subtle`}>Loading…</span>
        ) : (
          <span className={`font-mono text-[13px] text-destructive`}>
            Could not resolve this brand's platform — Domain Candidates can't be scoped safely.
          </span>
        )}
      </div>
    )
  }

  useEffect(() => {
    let cancelled = false
    // Fires once per full page load and, unlike this app's other API calls,
    // gates literally everything (every view falls back to "no access" until
    // it resolves) — so a single transient failure here (a cold worker, a
    // momentary blip in the role lookup) used to strand an actual Editor on
    // the denied screen for the rest of that tab's session, only fixable by
    // a hard reload. Retry a couple of times before accepting that as final.
    const attempt = async (retriesLeft: number): Promise<void> => {
      try {
        const res = await fetch('/api/my-access', { credentials: 'include' })
        const result = await res.json()
        if (cancelled) return
        if (!result?.success && retriesLeft > 0) {
          await new Promise((r) => setTimeout(r, 500))
          if (!cancelled) return attempt(retriesLeft - 1)
        }
        const data = result?.success ? result.data : null
        setAccess({
          isEditor: !!data?.isEditor,
          dashboardKeys: new Set((data?.dashboardKeys ?? []) as View[]),
          tenants: Array.isArray(data?.tenants) ? data.tenants : [],
        })
      } catch {
        if (cancelled) return
        if (retriesLeft > 0) {
          await new Promise((r) => setTimeout(r, 500))
          if (!cancelled) return attempt(retriesLeft - 1)
        }
        setAccess({ isEditor: false, dashboardKeys: new Set() })
      }
    }
    attempt(2)
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    const storedSidebarCollapsed = window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY)
    if (storedSidebarCollapsed === 'true') setSidebarCollapsed(true)
  }, [])

  useEffect(() => {
    const nextView = resolveView()
    setView(nextView)
    const nextBrand = brandForView(brands, nextView)
    if (nextBrand) {
      setOpenBrands((prev) => new Set(prev).add(nextBrand.id))
    }
    trackEvent({
      event_type: 'page',
      event_name: 'backoffice_view',
      page: 'BackofficeView',
      field_1_key: 'view',
      field_1_value: nextView,
      field_2_key: 'brand',
      field_2_value: nextBrand?.id ?? (isEntriesView(nextView) ? 'entries' : undefined),
    })
  }, [initialBrand, initialDashboard, initialEntriesTab])

  // navigateTo() updates the URL via history.pushState (see below) rather than
  // Next's router, so Back/Forward needs its own popstate handling to keep the
  // visible view in sync with the address bar.
  useEffect(() => {
    const onPopState = () => {
      const path = window.location.pathname
      const entriesMatch = path.match(/^\/entries\/([^/]+)/)
      if (entriesMatch) {
        setView(entriesViewFromTab(entriesMatch[1]) ?? 'entries-unified')
        return
      }
      const brandMatch = path.match(/^\/([^/]+)\/([^/]+)/)
      if (brandMatch) {
        setView(viewFromRoute(brands, brandMatch[1], brandMatch[2]))
      }
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  useEffect(() => {
    document.body.style.overflow = mobileNavOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [mobileNavOpen])

  // The header's "Collapse sidebar" button only renders when !mobileNavOpen
  // (see the aside's header JSX below) — below the lg breakpoint the sidebar
  // is a mobile overlay you open via the hamburger button, where "collapse
  // to a 48px rail" doesn't mean anything (there's no static-width grid
  // column to reclaim). Without this listener, opening that overlay on a
  // narrow/portrait viewport (setting mobileNavOpen=true) and then rotating
  // to landscape — crossing into lg: static-sidebar territory, exactly what
  // an iPad does — left mobileNavOpen stuck true, so the header kept
  // showing "Close" instead of "Collapse" even though the sidebar was now
  // the permanent desktop one where collapsing is the whole point.
  useEffect(() => {
    if (!window.matchMedia) return
    const mql = window.matchMedia('(min-width: 1024px)')
    const onChange = (e: MediaQueryListEvent) => { if (e.matches) setMobileNavOpen(false) }
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])

  const activeBrand = brandForView(brands, view)

  // Several backoffice tabs are usually open side by side (the env-colored
  // favicons in app/layout.tsx exist for the same reason), so the tab strip has
  // to say which view each one is. Keyed on `view` rather than the initial*
  // props so sidebar clicks, Back/Forward (the popstate listener above) and the
  // access fallback's navigateTo all move the title too; `brands` is in there
  // because a deep link into a generated brand only learns its label once the
  // platform catalog lands.
  useEffect(() => {
    setViewTitle(labelForView(brands, view))
  }, [brands, view])
  const accessibleBrands = access ? (access.isEditor ? brands : brands.filter((b) => brandHasGrantedView(b, access))) : []
  const visibleBrands = showAllBrands || !activeBrand ? accessibleBrands : [activeBrand]
  const isDark = theme === 'dark'
  const railOnly = sidebarCollapsed && !mobileNavOpen

  const handleThemeChange = useCallback((nextTheme: ProcessedTheme) => {
    setTheme(nextTheme)
  }, [setTheme])

  const handleSidebarCollapsedChange = useCallback((nextCollapsed: boolean) => {
    setSidebarCollapsed(nextCollapsed)
    window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(nextCollapsed))
  }, [])

  // The sidebar's health line: how many job workers are online (Editors
  // only -- /jobs/workers is an Editor endpoint), refreshed every minute.
  const [health, setHealth] = useState<{ online: number; total: number } | null>(null)
  useEffect(() => {
    if (!access?.isEditor) { setHealth(null); return }
    let cancelled = false
    const load = async () => {
      const r = await apiGet<Array<{ online: boolean }>>('/jobs/workers')
      if (cancelled || !r.success || !Array.isArray(r.data)) return
      setHealth({ online: r.data.filter((w) => w.online).length, total: r.data.length })
    }
    void load()
    const t = setInterval(() => { void load() }, 60_000)
    return () => { cancelled = true; clearInterval(t) }
  }, [access?.isEditor])

  // The topbar is sticky at the top of the content scroller; its height is
  // what every sticky bar below it (BaseDashboard's title bar, a table's
  // header) stacks under -- published through StickyStackContext.
  const topbarRef = useRef<HTMLDivElement>(null)
  const [topbarHeight, setTopbarHeight] = useState(63)
  useEffect(() => {
    const el = topbarRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setTopbarHeight(el.offsetHeight))
    ro.observe(el)
    setTopbarHeight(el.offsetHeight)
    return () => ro.disconnect()
  }, [access])

  const profile = useMemo(() => {
    if (!access) return { label: '…', detail: 'Resolving access' }
    return {
      label: access.isEditor ? 'Editor' : (access.tenants?.[0]?.name ?? 'Tenant'),
      detail: access.isEditor ? 'Operations' : (access.tenants?.map((t) => t.name).join(', ') || 'No tenant'),
    }
  }, [access])

  const navigateTo = (id: View) => {
    setView(id)
    setShowAllBrands(false)
    setMobileNavOpen(false)
    // Sidebar navigation is entirely client-state driven (view content never
    // depends on server-rendered data for the tab itself), so we only need
    // the URL bar to reflect the new route for deep-linking/refresh — not a
    // real Next.js navigation. router.push() here forces a round trip through
    // the edge-runtime [tab]/page.tsx Server Component on every click, which
    // under the Cloudflare Pages adapter can fall back to a full page reload
    // instead of a soft RSC transition (visible as "loads, then reloads").
    window.history.pushState(null, '', viewHref(brands, id))
    // pushState raises no popstate: tell a board with an open detail (useDetailPath) the URL moved
    window.dispatchEvent(new Event(BACKOFFICE_NAVIGATE_EVENT))
  }

  // The default `view` (resolved before /my-access returns) assumes Editor
  // access. Once access is known, bounce a non-Editor away from anything
  // they're not actually granted — to their first accessible brand, or
  // nowhere (content area shows a no-access message) if they have none.
  useEffect(() => {
    if (!access || isViewAllowed(brands, view, access)) return
    const firstAllowedBrand = brands.find((b) => access.isEditor || brandHasGrantedView(b, access))
    const fallbackView = firstAllowedBrand
      ? flatViews(firstAllowedBrand.views).find((v) => isViewAllowed(brands, v.id, access))?.id
      : undefined
    if (fallbackView) navigateTo(fallbackView)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access, view])

  // A notification carries a backoffice path (/viaverde/domain-candidates).
  // Route it like a sidebar click when it resolves to a view; a path this
  // build does not know, or one that names a record (/ctt/unified/{id} --
  // a sidebar click would land on the board, not the detail), falls back to
  // a real navigation.
  const openNotificationLink = (link: string) => {
    const m = /^\/([^/?#]+)\/([^/?#]+)(\/[^?#]+)?/.exec(link)
    if (m && m[1] !== 'entries' && !m[3]) {
      const resolved = viewFromRoute(brands, m[1], m[2])
      if (brands.some((b) => b.id === m[1] || b.platformCode === m[1])) { navigateTo(resolved); return }
    }
    window.location.assign(link)
  }

  const toggleBrand = (id: string) =>
    setOpenBrands((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  return (
    <PageChromeProvider>
    <NoticeProvider>
    <main className="h-screen overflow-hidden bg-background text-foreground transition-colors">
      <div className={`grid grid-cols-1 h-screen transition-[grid-template-columns] duration-200 ${sidebarCollapsed ? 'lg:grid-cols-[48px_1fr]' : 'lg:grid-cols-[232px_1fr]'}`}>
        {mobileNavOpen && (
          <div
            onClick={() => setMobileNavOpen(false)}
            className="fixed inset-0 z-30 bg-overlay lg:hidden"
          />
        )}
        <Sidebar
          theme={theme}
          onToggleTheme={() => handleThemeChange(isDark ? 'light' : 'dark')}
          view={view}
          hrefFor={(id) => viewHref(brands, id)}
          navigate={navigateTo}
          brands={visibleBrands}
          brandViewsFor={(brand) => (access ? grantedOnlyEntries(brand.views, access) : [])}
          logoFor={(brand) => entriesCatalog.platforms.find((p) => p.name_code === (brand.platformNameCode ?? brand.platformCode))?.logo_url ?? null}
          openBrands={openBrands}
          onToggleBrand={toggleBrand}
          activeBrand={activeBrand ?? null}
          showAllBrands={showAllBrands}
          onToggleShowAll={() => setShowAllBrands((v) => !v)}
          hiddenBrands={hiddenBrands}
          isEditor={!!access?.isEditor}
          operationsViews={access?.isEditor ? OPERATIONS_VIEWS : []}
          moderationViews={MODERATION_VIEWS}
          administrationViews={ADMIN_ONLY_VIEWS}
          onSignOut={onSignOut}
          collapsed={sidebarCollapsed}
          onCollapsedChange={handleSidebarCollapsedChange}
          mobileOpen={mobileNavOpen}
          onMobileClose={() => setMobileNavOpen(false)}
          bell={<NotificationsBell theme={theme} onOpen={openNotificationLink} />}
          bugReport={<BugReportWidget theme={theme} view={view} variant="sidebar" />}
          bugReportRail={<BugReportWidget theme={theme} view={view} variant="sidebar-collapsed" />}
          health={health}
          profile={profile}
        />

        {/* overflow-auto (not just -y) on purpose — this is the page's real
            scrolling ancestor that ResizableTable's sticky headers stick
            relative to (see the "No overflow-x wrapper here" comment in
            SibsMailView.tsx). Giving a table row's own local div its own
            overflow-x would make IT the nearest scroll container instead,
            breaking sticky headers — but this element was already the
            scrolling ancestor, so adding the x-axis here doesn't change
            that, it just makes wide content (tables especially) swipeable
            on mobile instead of silently overflowing past the card edge
            with no way to reach it. WebkitOverflowScrolling enables
            momentum/inertial touch-scrolling on iOS Safari, which has a
            long history of not engaging properly on plain overflow:auto
            without it, especially in nested-scroll layouts like this one.
            overscroll-x-contain addresses a separate iOS Safari behavior:
            a horizontal drag can get interpreted as the system's built-in
            "swipe from edge to go back/forward" navigation gesture instead
            of scrolling the page's own content — overscroll-behavior-x is
            the documented way to tell the browser this element owns
            horizontal panning here, so it stops treating the gesture as
            page navigation once this element is actually scrolling. */}
        <section
          className="min-w-0 overflow-auto overscroll-x-contain h-screen bg-background transition-colors"
          style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-x pan-y' }}
        >
          <Topbar
            ref={topbarRef}
            crumbs={crumbsForView(brands, view)}
            theme={theme}
            onToggleTheme={() => handleThemeChange(isDark ? 'light' : 'dark')}
            onOpenMenu={() => setMobileNavOpen(true)}
            bell={<NotificationsBell theme={theme} onOpen={openNotificationLink} />}
          />
          <StickyStackContext.Provider value={topbarHeight}>
          {!access ? null : !isViewAllowed(brands, view, access) ? (
            <div className={`flex h-full items-center justify-center text-sm text-muted-foreground`}>
              You don't have access to any backoffice area yet. Ask an admin to grant you access to a tenant.
            </div>
          ) : (
            <>
              {view === 'sibs-overview'      && <OverviewView brand="SIBS"      platformCode="mbway"     theme={theme} />}
              {view === 'sibs-mail'          && <CampaignsView theme={theme} />}
              {view === 'sibs-mail-entries'  && <CampaignsView theme={theme} entriesOnly />}
              {view === 'sibs-alerts'        && <AlertsAnalysisView theme={theme} />}
              {view === 'sibs-pilot-overview' && <SibsPilotOverviewView theme={theme} />}
              {/* The three shared templates, for every brand at once: they are
                  identical but for the platform_id they scope to, and the brand
                  list is now the platform table, so enumerating them per brand
                  would mean editing this file every time a platform is added.
                  Guarded on isEntriesView because 'entries-cases' (the general,
                  unscoped one) also ends in '-cases' and is rendered below. */}
              {!isEntriesView(view) && defaultKindForView(view) === 'integrity' && renderBrandIntegrity(brandCodeForView(view))}
              {!isEntriesView(view) && defaultKindForView(view) === 'cases'   && renderBrandCases(brandCodeForView(view))}
              {!isEntriesView(view) && defaultKindForView(view) === 'unified' && renderBrandUnified(brandCodeForView(view))}
              {!isEntriesView(view) && defaultKindForView(view) === 'domains' && renderBrandDomains(brandCodeForView(view))}
              {view === 'ctt-site'           && <SitePreviewView url={process.env.NEXT_PUBLIC_CTT_SITE_URL ?? 'http://localhost:4003'} label="CTT" />}
              {view === 'ctt-mail-alert'     && <CttMailAlertView theme={theme} />}
              {view === 'ctt-processed'      && <ProcessedEntriesView platformCode="ctt"       label="CTT · Processed Entries"       filterPostType="text" theme={theme} onThemeChange={handleThemeChange} />}
              {view === 'ctt-scalls'         && <ProcessedEntriesView platformCode="ctt"       label="CTT · AI Signals"                  excludePostType="text" showChart theme={theme} onThemeChange={handleThemeChange} />}
              {view === 'ctt-trustcenter'    && <CttTrustCenterView theme={theme} />}
              {view === 'ctt-trustcenter-entries' && <CttTrustCenterEntriesView theme={theme} />}
              {view === 'ctt-raw-inbound-email' && <CasesDashboardView theme={theme} sourceType="ctt_trustfence_email" expectedPlatformCode="ctt" storageKey="ctt-raw-inbound-email" isEditor={access?.isEditor} />}
              {view === 'ctt-vendor-entries' && <CttVendorEntriesView theme={theme} isEditor={access?.isEditor} />}
              {view === 'santander-overview' && <OverviewView brand="Santander" platformCode="santander" theme={theme} />}
              {view === 'santander-processed'&& <ProcessedEntriesView platformCode="santander" label="Santander · Processed Entries" theme={theme} onThemeChange={handleThemeChange} />}
              {view === 'cgd-overview'       && <OverviewView brand="CGD"       platformCode="cgd"       theme={theme} />}
              {view === 'cgd-processed'      && <ProcessedEntriesView platformCode="cgd"       label="CGD · Processed Entries" theme={theme} onThemeChange={handleThemeChange} />}
              {view === 'entries-cases'       && <CasesDashboardView theme={theme} storageKey="entries" isEditor={access?.isEditor} />}
              {view === 'entries-unified'     && <UnifiedEntriesDashboardView theme={theme} storageKey="entries" isEditor={access?.isEditor} />}
              {view === 'entries-campaigns'   && <CampaignsDashboardView theme={theme} storageKey="entries" isEditor={access?.isEditor} />}
              {view === 'entries-domains'     && <DomainCandidatesDashboardView theme={theme} storageKey="entries-domains" isEditor={access?.isEditor} />}
              {view === 'entries-casos'       && <CasosView theme={theme} catalog={entriesCatalog} catalogLoading={entriesCatalogLoading} />}
              {view === 'entries-plataformas-manage' && <ManagePlatformsView theme={theme} />}
              {view === 'entries-brand-vocabulary' && <BrandVocabularyView theme={theme} />}
              {view === 'entries-token-chain' && <TokenChainView theme={theme} />}
              {view === 'entries-sources-manage' && <ManageSourcesView theme={theme} />}
              {view === 'entries-tenants-manage' && <ManageTenantsView theme={theme} />}
              {view === 'entries-tenant-logs' && <TenantLogsView theme={theme} />}
              {view === 'entries-email-types' && <ManageEmailTypesView theme={theme} />}
              {view === 'entries-email-logs' && <EmailLogsView theme={theme} />}
              {view === 'entries-email-compose' && <EmailComposeView theme={theme} />}
              {view === 'entries-submit-entry' && <AdminSubmitEntryView theme={theme} />}
              {view === 'entries-jobs'         && <JobsView theme={theme} />}
              {view === 'entries-actions'      && <ActionsView theme={theme} />}
              {view === 'entries-ui-kit'       && <UiKitView theme={theme} />}
            </>
          )}
          </StickyStackContext.Provider>
        </section>
      </div>
    </main>
    </NoticeProvider>
    </PageChromeProvider>
  )
}

export function BackofficeAccessDenied() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-6 text-foreground">
      <div className="max-w-md border border-destructive/40 bg-card p-6 shadow-sm">
        <div className="mb-3 flex h-10 w-10 items-center justify-center border border-destructive/40 bg-danger-surface text-destructive">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <h1 className="text-2xl font-semibold">Acesso negado</h1>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          A sessão SuperTokens existe, mas não cumpre os requisitos de acesso ao backoffice.
        </p>
      </div>
      <BugReportWidget theme="light" />
    </main>
  )
}

export function BackofficeApp({ initialBrand, initialDashboard, initialEntriesTab }: { initialBrand?: string; initialDashboard?: string; initialEntriesTab?: string } = {}) {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    ensureBackofficeSuperTokens()
    setReady(true)
  }, [])

  const signOut = async () => {
    trackEvent({ event_type: 'action', event_name: 'sign_out', component: 'sidebar_nav' })
    await superTokensSignOut()
    window.location.assign('/login')
  }

  if (!ready) return <main className="min-h-screen bg-background" />

  return (
    <SuperTokensWrapper>
      {/* No global "Editor" claim requirement here anymore — that would block
          the tenant-scoped, non-Editor accounts this shell now also serves.
          Any authenticated session reaches Shell; which brands/sections it
          actually gets to see is resolved from /my-access below and enforced
          again per-request by each backend route. */}
      <SessionAuth accessDeniedScreen={BackofficeAccessDenied}>
        <Shell onSignOut={signOut} initialBrand={initialBrand} initialDashboard={initialDashboard} initialEntriesTab={initialEntriesTab} />
      </SessionAuth>
    </SuperTokensWrapper>
  )
}

// ─── CTT Shared Utilities ────────────────────────────────────────────────────
// fmtDatePt, processedToPreview, CttPreview → TrustCenter/CTT/
// SIBS Custom Mail Alerts → SibsMailView.tsx

// ─── CTT Processed Entries ───────────────────────────────────────────────────

const TURNSTILE_SITEKEY = process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_TF_CENTER_GETENTRIES_KEY ?? ''


const PROCESSED_ENTRY_COLUMNS: ColumnDef[] = [
  { key: 'chevron', label: '', thClassName: 'w-7' },
  { key: 'user_entry_id', label: 'User_Entry_ID', initialWidth: 108 },
  { key: 'title', label: 'Title' },
  { key: 'created_at', label: 'Created_At' },
  { key: 'post_type', label: 'Post_Type' },
  { key: 'domain', label: 'Domain' },
  { key: 'domain_status', label: 'Status' },
  { key: 'sender', label: 'Sender' },
  { key: 'message_content', label: 'Message_Content', thClassName: 'min-w-[280px]' },
  { key: 'is_scam', label: 'Is_Scam' },
]


const EXPANSION_DOMAIN_COLUMNS: ColumnDef[] = [
  { key: 'entry_domain_id', label: 'Entry Domain ID' },
  { key: 'original_url', label: 'Original URL' },
  { key: 'domain_id', label: 'Domain ID' },
  { key: 'subdomain', label: 'Subdomain' },
  { key: 'manual_inactive', label: 'Manual inactive' },
  { key: 'is_active', label: 'Is active', thTitle: 'raw_domain_metadata.is_active (WHOIS)' },
]

const EXPANSION_BROWSER_COLUMNS: ColumnDef[] = [
  { key: 'id', label: 'ID' },
  { key: 'entry_domain_id', label: 'Entry Domain ID' },
  { key: 'captured_at', label: 'Captured at' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'desktop', label: 'Desktop' },
  { key: 'raw_data', label: 'Raw data' },
  { key: 'screenshot', label: 'Screenshot' },
]

const EXPANSION_PROBE_COLUMNS: ColumnDef[] = [
  { key: 'captured_at', label: 'Captured at' },
  { key: 'node', label: 'Node' },
  { key: 'ip', label: 'Probe IP' },
  { key: 'triggered_by', label: 'Triggered by' },
  { key: 'tests', label: 'Tests' },
  { key: 'details', label: 'Details' },
]

const EXPANSION_TH_ROW = 'bg-secondary font-mono text-[9px] uppercase tracking-widest text-muted-foreground'
const EXPANSION_TABLE_CLS = 'w-full text-[11px] border border-border'
const EXPANSION_TH_PADDING = 'px-3 py-1.5'


// ─── Processed entry watch interface ─────────────────────────────────────────


function WatchStatCard({ label, value, delta, caption, theme, tone = 'neutral' }: {
  label: string
  value: string
  delta?: string
  caption: string
  theme: ProcessedTheme
  tone?: 'neutral' | 'red' | 'green' | 'amber'
}) {
  const c = watchClasses(theme)
  const deltaClass = tone === 'red' ? 'text-destructive' : tone === 'green' ? 'text-success' : tone === 'amber' ? 'text-warning' : c.muted
  return (
    <div className={`border px-6 py-6 ${c.card}`}>
      <div className={`font-mono text-[11px] uppercase tracking-[0.28em] ${c.label}`}>{label}</div>
      <div className="mt-4 flex items-baseline gap-2 font-mono">
        <span className="text-[42px] leading-none tracking-tight">{value}</span>
        {delta && <span className={`text-sm font-semibold ${deltaClass}`}>{delta}</span>}
      </div>
      <div className={`mt-4 text-sm ${c.muted}`}>{caption}</div>
    </div>
  )
}


// ─── ProcessedEntriesView ────────────────────────────────────────────────────
function ProcessedEntriesView({
  platformCode,
  label,
  filterPostType,
  excludePostType,
  showChart,
  theme,
  onThemeChange,
}: {
  platformCode: string
  label: string
  filterPostType?: string
  excludePostType?: string
  showChart?: boolean
  theme: ProcessedTheme
  onThemeChange: (theme: ProcessedTheme) => void
}) {
  const [entries, setEntries] = useState<ProcessedEntry[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<ProcessedEntry | null>(null)
  const [selectedDomainCreatedAt, setSelectedDomainCreatedAt] = useState<string | undefined>()
  const [typeFilter, setTypeFilter] = useState<string | null>(null)
  const [detailEntry, setDetailEntry] = useState<ProcessedEntry | null>(null)
  const [detailScreenshotReady, setDetailScreenshotReady] = useState(false)
  const turnstileRef = useRef<TurnstileHandle>(null)
  const screenshotTurnstileRef = useRef<TurnstileHandle>(null)
  const pendingTokenRef = useRef<((token: string) => void) | null>(null)

  const handleScreenshotVerify = useCallback((token: string) => {
    pendingTokenRef.current?.(token)
    pendingTokenRef.current = null
  }, [])

  const getScreenshotToken = useCallback((): Promise<string> => {
    return new Promise((resolve) => {
      pendingTokenRef.current = resolve
      const runWhenReady = (attempt = 0) => {
        if (screenshotTurnstileRef.current) {
          screenshotTurnstileRef.current.reset()
          screenshotTurnstileRef.current.run()
          return
        }
        if (attempt < 20) setTimeout(() => runWhenReady(attempt + 1), 100)
      }
      runWhenReady()
    })
  }, [])

  const handleVerify = useCallback(async (token: string) => {
    setError(null)
    try {
      const res = await fetch('/api/campaigns/processed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ turnstileToken: token, platformCode }),
      })
      const payload = await res.json()
      if (payload?.success) {
        const loaded: ProcessedEntry[] = payload.data.entries ?? []
        setEntries(loaded)
        setTotal(payload.data.total ?? 0)
      } else {
        setError(payload?.reason ?? payload?.error ?? 'Failed to load data')
      }
    } catch {
      setError('Network error')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [platformCode])

  const handleTurnstileError = useCallback(() => {
    setLoading(false)
    setRefreshing(false)
    setError('Verification failed — try refreshing')
  }, [])

  const handleToggleInactive = useCallback(async (userEntryDomainId: string, next: boolean) => {
    setEntries((prev) =>
      prev.map((e) => e.userEntryDomainId === userEntryDomainId ? { ...e, manualInactive: next } : e),
    )
    setDetailEntry((current) =>
      current?.userEntryDomainId === userEntryDomainId ? { ...current, manualInactive: next } : current,
    )
    try {
      await fetch('/api/campaigns/ctt/domain-inactive', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEntryDomainId, manualInactive: next }),
      })
    } catch {
      // revert on failure
      setEntries((prev) =>
        prev.map((e) => e.userEntryDomainId === userEntryDomainId ? { ...e, manualInactive: !next } : e),
      )
      setDetailEntry((current) =>
        current?.userEntryDomainId === userEntryDomainId ? { ...current, manualInactive: !next } : current,
      )
    }
  }, [])

  const handleRefresh = () => {
    setRefreshing(true)
    setError(null)
    setLoading(true)
    turnstileRef.current?.reset()
    turnstileRef.current?.run()
  }

  useEffect(() => {
    if (!detailEntry) {
      setDetailScreenshotReady(false)
      return
    }
    setDetailScreenshotReady(false)
    const timer = window.setTimeout(() => setDetailScreenshotReady(true), 700)
    return () => window.clearTimeout(timer)
  }, [detailEntry])

  const scoped = useMemo(
    () =>
      entries.filter((e) => {
        if (filterPostType && e.postType !== filterPostType) return false
        if (excludePostType && e.postType === excludePostType) return false
        return true
      }),
    [entries, filterPostType, excludePostType],
  )

  const typeCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const e of scoped) {
      const key = e.postType ?? 'unknown'
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    return counts
  }, [scoped])

  const filtered = useMemo(
    () =>
      scoped.filter((e) => {
        if (typeFilter && (e.postType ?? 'unknown') !== typeFilter) return false
        if (!query) return true
        const q = query.toLowerCase()
        return (
          (e.headline ?? '').toLowerCase().includes(q) ||
          (e.title ?? '').toLowerCase().includes(q) ||
          (e.messageContent ?? '').toLowerCase().includes(q) ||
          (e.sender ?? '').toLowerCase().includes(q)
        )
      }),
    [scoped, query, typeFilter],
  )

  const chartData = useMemo(() => showChart ? buildPeChartData(scoped) : [], [scoped, showChart])
  const allTimeChartData = useMemo(() => showChart ? buildAllTimeChartData(scoped) : [], [scoped, showChart])

  const c = watchClasses(theme)
  const activeDomains = scoped.filter((e) => e.lastMobileIsAlive === true || e.lastDesktopIsAlive === true || e.lastProbeIsAlive === true).length
  const reports24h = scoped.filter((e) => Date.now() - new Date(e.createdAt).getTime() <= 86400000).length
  const activeNarratives = new Set(scoped.map((e) => entryDisplayTitle(e))).size
  const captureCount = scoped.reduce((sum, e) => sum + (e.browserDataCount ?? 0), 0)
  const title = showChart ? `${platformCode.toUpperCase()} AI signals under watch` : 'Phishing campaigns under watch'
  const shownInfo = filtered.length === scoped.length ? `${filtered.length} shown` : `${filtered.length} of ${scoped.length} shown`

  if (loading) {
    return (
      <>
        {loading && <Turnstile ref={turnstileRef} sitekey={TURNSTILE_SITEKEY} onVerify={handleVerify} onError={handleTurnstileError} onTimeout={handleTurnstileError} autoRun />}
        <div className={`flex h-screen items-center justify-center ${c.shell}`}>
          <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground animate-pulse">Loading…</span>
        </div>
      </>
    )
  }

  if (error) {
    return (
      <div className={`flex h-screen items-center justify-center gap-3 text-destructive ${c.shell}`}>
        <AlertCircle className="h-5 w-5" />
        <span className="font-mono text-[13px]">{error}</span>
      </div>
    )
  }

  if (detailEntry) {
    return (
      <>
        <Turnstile ref={screenshotTurnstileRef} sitekey={TURNSTILE_SITEKEY} onVerify={handleScreenshotVerify} />
        <div className={`min-h-screen px-6 py-6 font-sans transition-colors ${c.shell}`}>
          <div className="mx-auto max-w-[1280px]">
            <button
              onClick={() => setDetailEntry(null)}
              className={`mb-5 inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium ${c.line} ${c.card}`}
            >
              <ChevronLeft className="h-4 w-4" />
              Back to processed entries
            </button>
            {detailScreenshotReady ? (
              <ProcessedEntryCard
                entry={detailEntry}
                expanded
                detailMode
                theme={theme}
                query={query}
                onToggle={() => null}
                onOpenModal={(modalEntry, dc) => { setSelected(modalEntry); setSelectedDomainCreatedAt(dc) }}
                onToggleInactive={handleToggleInactive}
                getToken={getScreenshotToken}
              />
            ) : (
              <div className={`border px-7 py-10 ${c.card}`}>
                <span className={`font-mono text-[11px] uppercase tracking-widest ${c.faint}`}>Preparing screenshot verification...</span>
              </div>
            )}
          </div>
        </div>

        <ThreatDetailModal
          key={selected?.id ?? 'none'}
          entry={selected}
          domainCreatedAt={selectedDomainCreatedAt}
          onClose={() => { setSelected(null); setSelectedDomainCreatedAt(undefined) }}
          getToken={getScreenshotToken}
          config={{ apiBase: '' }}
        />
      </>
    )
  }

  return (
    <>
      <Turnstile ref={screenshotTurnstileRef} sitekey={TURNSTILE_SITEKEY} onVerify={handleScreenshotVerify} />
      <div className={`min-h-screen px-6 py-10 font-sans transition-colors ${c.shell}`}>
        <div className="mx-auto max-w-[1280px]">
          <header>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className={`font-mono text-[11px] uppercase tracking-[0.28em] ${c.label}`}>// {label}</div>
                <h1 className="mt-4 text-[34px] font-semibold leading-tight tracking-[-0.02em]">{title}</h1>
                <p className={`mt-3 max-w-3xl text-[17px] leading-7 ${c.muted}`}>
                  User-submitted fraud reports processed for the {platformCode.toUpperCase()} platform. Click any row to inspect domain intelligence, browser probes, and probe-run history.
                </p>
              </div>
            </div>

            <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
              <WatchStatCard label="Active narratives" value={String(activeNarratives).padStart(2, '0')} delta={activeNarratives > 0 ? `+${Math.min(activeNarratives, 4)}` : undefined} caption={`across ${typeCounts.size || 1} source types`} theme={theme} tone="amber" />
              <WatchStatCard label="Reports (24h)" value={(reports24h || total).toLocaleString('en-US')} delta={reports24h > 0 ? '+ live' : undefined} caption="ingested from discourse" theme={theme} tone="red" />
              <WatchStatCard label="Active domains" value={String(activeDomains).padStart(2, '0')} delta={activeDomains > 0 ? `- ${Math.max(scoped.length - activeDomains, 0)}` : undefined} caption="under live monitoring" theme={theme} tone="green" />
              <WatchStatCard label="Browser captures" value={String(captureCount).padStart(2, '0')} caption="available screenshots and probes" theme={theme} />
            </div>
          </header>

          <section className={`mt-9 overflow-hidden border ${c.card}`}>
            <div className={`flex flex-wrap items-center gap-4 border-b px-7 py-5 ${c.line}`}>
              <div className={`flex h-9 w-9 items-center justify-center rounded-md border border-signal/30 bg-signal-surface text-signal`}>
                <Archive className="h-4 w-4" />
              </div>
              <div>
                <h2 className="text-base font-semibold">Narratives parsed from discourse</h2>
                <p className={`text-xs ${c.muted}`}>Updated now · {shownInfo}</p>
              </div>
              <div className="ml-auto flex min-w-[280px] flex-1 items-center gap-2 sm:max-w-xl">
                <div className={`flex h-9 min-w-0 flex-1 items-center gap-2 rounded-md border px-3 ${c.input}`}>
                  <Search className="h-4 w-4 shrink-0 opacity-60" />
                  <input
                    placeholder="Filter by title, sender, message..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-inherit"
                  />
                </div>
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  title="Refresh"
                  className={`flex h-9 w-9 items-center justify-center rounded-md border ${c.line} ${c.soft} disabled:opacity-50`}
                >
                  <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {typeCounts.size > 1 && !filterPostType && (
              <div className={`flex flex-wrap gap-2 border-b px-7 py-3 ${c.line}`}>
                <button
                  onClick={() => setTypeFilter(null)}
                  className={`rounded-md border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest ${typeFilter === null ? 'border-signal bg-signal text-white' : `${c.line} ${c.soft} ${c.muted}`}`}
                >
                  All <span className="ml-1 opacity-70">{scoped.length}</span>
                </button>
                {Array.from(typeCounts.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([type, count]) => (
                  <button
                    key={type}
                    onClick={() => setTypeFilter(typeFilter === type ? null : type)}
                    className={`rounded-md border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest ${typeFilter === type ? 'border-signal bg-signal text-white' : `${c.line} ${c.soft} ${c.muted}`}`}
                  >
                    {postTypeLabel(type)} <span className="ml-1 opacity-70">{count}</span>
                  </button>
                ))}
              </div>
            )}

            {showChart && (
              <div className={`border-b px-7 py-5 ${c.line}`}>
                <p className={`mb-3 font-mono text-[11px] uppercase tracking-widest ${c.label}`}>AI signals · last 30 days</p>
                <ResponsiveContainer width="100%" height={160}>
                  <AreaChart data={chartData} margin={{ top: 4, right: 0, left: -24, bottom: 0 }}>
                    <defs>
                      <linearGradient id={`g-signals-${theme}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--destructive)" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="var(--destructive)" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" tick={{ fill: 'var(--subtle-text)', fontSize: 9, fontFamily: 'monospace' }} tickLine={false} axisLine={false} interval={4} />
                    <YAxis tick={{ fill: 'var(--subtle-text)', fontSize: 9, fontFamily: 'monospace' }} tickLine={false} axisLine={false} />
                    <Tooltip content={<PeChartTooltip />} />
                    <Area type="monotone" dataKey="signals" stroke="var(--destructive)" strokeWidth={1.5} fill={`url(#g-signals-${theme})`} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            {filtered.length === 0 ? (
              <div className={`px-7 py-14 text-center font-mono text-[11px] uppercase tracking-widest ${c.faint}`}>No entries found</div>
            ) : (
              filtered.map((entry) => (
                <ProcessedEntryCard
                  key={entry.id}
                  entry={entry}
                  expanded={false}
                  theme={theme}
                  query={query}
                  onToggle={() => { setDetailScreenshotReady(false); setDetailEntry(entry) }}
                  onOpenModal={(modalEntry, dc) => { setSelected(modalEntry); setSelectedDomainCreatedAt(dc) }}
                  onToggleInactive={handleToggleInactive}
                  getToken={getScreenshotToken}
                />
              ))
            )}
          </section>

          {showChart && allTimeChartData.length > 0 && (
            <section className={`mt-5 border px-7 py-5 ${c.card}`}>
              <p className={`mb-3 font-mono text-[11px] uppercase tracking-widest ${c.label}`}>All-time signal activity</p>
              <ResponsiveContainer width="100%" height={140}>
                <AreaChart data={allTimeChartData} margin={{ top: 4, right: 0, left: -24, bottom: 0 }}>
                  <XAxis dataKey="label" tick={{ fill: 'var(--subtle-text)', fontSize: 9, fontFamily: 'monospace' }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fill: 'var(--subtle-text)', fontSize: 9, fontFamily: 'monospace' }} tickLine={false} axisLine={false} />
                  <Tooltip content={<PeChartTooltip />} />
                  <Area type="natural" dataKey="signals" stroke="var(--destructive)" strokeWidth={1.5} fill="#ef44441a" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </section>
          )}
        </div>
      </div>

      <ThreatDetailModal
        key={selected?.id ?? 'none'}
        entry={selected}
        domainCreatedAt={selectedDomainCreatedAt}
        onClose={() => { setSelected(null); setSelectedDomainCreatedAt(undefined) }}
        getToken={getScreenshotToken}
        config={{ apiBase: '' }}
      />
    </>
  )
}


function SitePreviewView({ url, label }: { url: string; label: string }) {
  const [path, setPath] = useState('/')
  const [committed, setCommitted] = useState('/')
  const [refreshSignal, setRefreshSignal] = useState(0)
  const fullUrl = url.replace(/\/$/, '') + committed

  return (
    <div className="flex h-screen flex-col">
      <div className="flex items-center gap-2 border-b border-border bg-card px-4 py-2 shrink-0">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">
          {label} · Site preview
        </div>
        <div className="flex flex-1 items-center gap-1 rounded border border-border bg-secondary px-2 py-1 font-mono text-[11px]">
          <span className="shrink-0 text-muted-foreground">{url}</span>
          <input
            value={path}
            onChange={(e) => setPath(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') setCommitted(path) }}
            className="min-w-0 flex-1 bg-transparent text-muted-foreground outline-none"
          />
        </div>
        <a
          href={fullUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex shrink-0 items-center gap-1 border border-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground transition-colors hover:border-signal hover:text-signal"
        >
          Open ↗
        </a>
        <button
          onClick={() => setRefreshSignal((value) => value + 1)}
          title="Refresh preview"
          className="flex h-7 w-7 shrink-0 items-center justify-center border border-border text-muted-foreground transition-colors hover:border-signal hover:text-signal"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>
      <iframe
        key={`${fullUrl}-${refreshSignal}`}
        src={fullUrl}
        className="flex-1 w-full border-0"
        title={`${label} site preview`}
      />
    </div>
  )
}
